import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

// Компонент для рендера блочных математических формул через KaTeX.
// Поддерживает многострочные формулы, заданные через команду \displaylines{...}.

const useStyles = makeStyles(() => ({
  root: {
    // Выравниваем формулу по левому краю и убираем внешние отступы KaTeX
    "& .katex-display": {
      textAlign: "left",
      margin: 0,
    },
  },
}));

/**
 * Приводит LaTeX-строку к виду, совместимому с KaTeX.
 *
 * KaTeX (в отличие от нативного TeX) не принимает группу `{...}` в качестве
 * разделителя для команд задания размера разделителей (`\bigl`, `\bigr`,
 * `\bigm`, `\Bigl` и т.п.). Запись вида `\bigm{|}`, корректная в TeX,
 * вызывает ошибку `Invalid delimiter type 'ordgroup'`. Здесь такие скобки
 * вокруг одиночного токена-разделителя снимаются: `\bigm{|}` → `\bigm|`.
 *
 * @param {string} latex - Исходная LaTeX-строка.
 * @returns {string} Нормализованная LaTeX-строка, безопасная для KaTeX.
 */
const normalizeForKatex = (latex) =>
  latex.replace(
    /\\(big|Big|bigg|Bigg|bigl|Bigl|biggl|Biggl|bigr|Bigr|biggr|Biggr|bigm|Bigm|biggm|Biggm)\{(\||\\vert|\\lvert|\\rvert|\\\||\\Vert|\\lVert|\\rVert)\}/g,
    "\\$1$2",
  );

/** Разбирает конструкцию \displaylines{...} на отдельные строки формулы.
 Если \displaylines не используется, возвращает исходную строку как единственный элемент массива.
 */
const splitDisplayLines = (latex) => {
  if (!latex) return [];

  const match = latex.match(/\\displaylines\s*\{([\s\S]*)}$/);
  if (!match) {
    return [normalizeForKatex(latex)];
  }

  const body = match[1] || "";
  return body
    .split(/\\\\/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map(normalizeForKatex);
};

/**
 * Компонент для рендера блочных математических формул через KaTeX.
 * Поддерживает многострочные формулы, заданные через команду \displaylines{...}.
 */
const MathBlock = ({ latex, className }) => {
  const classes = useStyles();
  const lines = splitDisplayLines(latex);

  if (!lines.length) return null;

  return (
    <div className={`${classes.root} ${className || ""}`}>
      {lines.map((line, index) => (
        <BlockMath key={index} math={line} />
      ))}
    </div>
  );
};

MathBlock.propTypes = {
  // LaTeX-строка с формулой, пришедшая с сервера
  latex: PropTypes.string.isRequired,
  // Дополнительный CSS-класс обёртки, чтобы подстроиться под окружение
  className: PropTypes.string,
};

export default MathBlock;
